import React, { FC, ReactElement, ReactNode, useEffect, useState } from "react"
import { Fade } from "react-awesome-reveal"
import moment from "moment"
import "styles/react-datetime.css"

import Firebase, {
    withFirebase,
    User,
    ParkingSpot,
    Reservation,
    VisitorReservation,
    Car,
    UserPriorityLevel,
    Location,
    EngineType,
    ReservationStatus
} from "../../components/Firebase"
import { UserStateCheck } from "../../shared-components/Session"
import Utilities from "../Firebase/Models-dist/utilities"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/free-solid-svg-icons"

import $ from "jquery"

import Swal from "sweetalert2/dist/sweetalert2.js"
import withReactContent from "sweetalert2-react-content"
import { Link, createSearchParams } from "react-router-dom"
import * as ROUTES from "constants/routes"
import { DateCalendar, DateField, LocalizationProvider } from "@mui/x-date-pickers"
import { IntlShape, injectIntl } from "react-intl"
import { Button, Theme, ThemeProvider } from "@mui/material"
import muiTheme from "otherFiles/muiTheme"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { languages } from "../../constants/languages"
import DeleteForever from "@mui/icons-material/DeleteForever"

const MySwal = withReactContent(Swal)

interface IVisitorsBookingBaseState {
    parkingSpots?: ParkingSpot[]
    allowedLocations?: Location[]
    visitorsReservations?: VisitorReservation[]
    invitedByUsers?: User[]
}

interface IVisitorsBookingBaseProps {
    user: User
    firebase: Firebase
    intl: IntlShape
}

class VisitorsBookingBase extends React.Component<IVisitorsBookingBaseProps, IVisitorsBookingBaseState> {
    tomorrow = moment.tz(this.timezone).add(1, "d")
    fromDate = this.tomorrow?.set({ h: 8, m: 0, s: 0, ms: 0 })
    toDate = this.tomorrow?.set({ h: 16, m: 0, s: 0, ms: 0 })

    get timezone() {
        return this.props.user.activeLocation?.timezone ?? "Europe/Prague"
    }

    constructor(props: IVisitorsBookingBaseProps) {
        super(props)

        this.state = {
            parkingSpots: undefined,
            visitorsReservations: undefined
        }
    }

    componentDidMount() {
        this.updateData()
        this.fetchAdminPrivileges()
    }

    updateData() {
        this.setState({ visitorsReservations: undefined })
        this.fetchParkingSpots()
        this.fetchReservations()
    }

    onFromDateChange = (date: string | moment.Moment | null) => {
        if (date === null) {
            return
        }
        const fromDate = this.onDateChange(date)
        if (fromDate) {
            this.fromDate = fromDate
            this.toDate = fromDate
        }
    }
    onToDateChange = (date: string | moment.Moment | null) => {
        if (date === null) {
            return
        }
        const toDate = this.onDateChange(date)
        if (toDate) {
            this.toDate = toDate
        }
    }
    onDateChange = (date: moment.Moment | string) => {
        const filterDate = moment.isMoment(date) ? date : moment(date)
        if (filterDate.isValid()) {
            return filterDate
        } else {
            return null
        }
    }

    fetchReservations = async () => {
        const { user, firebase } = this.props

        if (!user.uid || !user.activeLocation) {
            return Promise.reject(new Error("User has no uid or activeLocation"))
        }

        try {
            const startDate = moment().startOf("d").toDate()
            const endDate = moment().add(3, "M").toDate()
            const { visitorsReservations, invitedByUsers } = await firebase.fetchVisitorsReservations(user.activeLocation, startDate, endDate, true)

            this.setState({ visitorsReservations, invitedByUsers })
        } catch (e) {
            console.warn("Error fetching reservations: ", e)
        }
    }

    fetchParkingSpots = () => {
        this.props.firebase
            .fetchParkingSpots(this.props.user.activeLocationId, true)
            .then(parkingSpots => {
                parkingSpots.sort((a, b) => a.name.localeCompare(b.name))
                this.setState({ parkingSpots })
            })
            .catch(error => {
                console.warn("Error getting parking spots: ", error)
            })
    }

    onSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (event.target.name === "location") {
            this.props.user.activeLocationId = event.target.value
            this.props.firebase.setUserActiveLocation(this.props.user, event.target.value)
            this.updateData()
        }
    }

    fetchAdminPrivileges = async () => {
        try {
            const privileges = await this.props.firebase.fetchAdminPrivileges(this.props.user.uid)
            const locations = (await Promise.all(privileges.locationsIds.map(this.props.firebase.fetchLocation))).filter(l => l).map(l => l!)
            this.setState({ allowedLocations: locations })
        } catch (error) {
            this.setState({ allowedLocations: [] })
            console.warn("Error fetching admin privileges: ", error)
        }
    }

    getAvailableSpotsForSelect = () => {
        if (this.props.user.activeLocation === undefined || this.toDate === undefined) {
            return Promise.reject(new Error("User has no activeLocation!"))
        }
        const promises: Promise<ParkingSpot[]>[] = []
        let startTime = moment(this.fromDate)
        const end = this.toDate
        while (startTime <= end) {
            let endTime = moment(startTime).endOf("d")
            if (endTime > end) {
                endTime = end
            }
            promises.push(this.props.firebase.fetchAvailableSpots(this.props.user.activeLocation, startTime.toDate(), endTime.toDate(), true))
            startTime = startTime.add(1, "d").startOf("d")
        }
        return Promise.all(promises).then(spotSets => {
            if (spotSets.length === 0) {
                return Promise.resolve([])
            }
            const availableSpots = spotSets.reduce((acc, x) => intersection(acc, x), spotSets[0])
            return Promise.resolve(availableSpots.reduce((a, spot) => ({ ...a, [spot.id!]: spot.name }), {}))
        })
    }

    handleAddBookingClick = () => {
        let spotId: string = ""
        MySwal.fire({
            title: "Select reservation start date",
            html: (
                <StaticDatePicker
                    date={moment(this.fromDate)}
                    onChange={this.onFromDateChange}
                    timezone={this.props.user.activeLocation?.timezone}
                    locale={this.props.intl.locale}
                    theme={muiTheme}
                />
            ),
            confirmButtonText: "Next &rarr;",
            showCancelButton: true
        })
            .then(result => {
                if (result.isDismissed) {
                    //dismissed dialog
                    return Promise.reject(new Error("closed"))
                }
                return MySwal.fire({
                    title: "Select reservation start time",
                    html: <input className="y-style" id="time-input" defaultValue="08:00" type="time" />,
                    confirmButtonText: "Next &rarr;",
                    showCancelButton: true,
                    preConfirm: () => {
                        return (document!.getElementById("time-input") as HTMLInputElement).value
                    }
                })
            })
            .then(result => {
                if (result.isDismissed) {
                    //dismissed dialog
                    return Promise.reject(new Error("closed"))
                }
                if (this.props.user.activeLocation === undefined || this.fromDate === undefined) {
                    return Promise.reject(new Error("User has no activeLocation!"))
                }
                this.fromDate = Utilities.parseTime(result.value!, this.fromDate.toDate(), this.props.user.activeLocation.timezone)
                return MySwal.fire({
                    title: "Select reservation end date",
                    html: (
                        <StaticDatePicker
                            date={moment(this.toDate)}
                            onChange={this.onToDateChange}
                            timezone={this.props.user.activeLocation.timezone}
                            locale={this.props.intl.locale}
                            theme={muiTheme}
                        />
                    ),
                    confirmButtonText: "Next &rarr;",
                    showCancelButton: true
                })
            })
            .then(result => {
                if (result.isDismissed) {
                    //dismissed dialog
                    return Promise.reject(new Error("closed"))
                }
                return MySwal.fire({
                    title: "Select reservation end time",
                    html: <input className="y-style" id="time-input" defaultValue="16:00" type="time" />,
                    confirmButtonText: "Next &rarr;",
                    showCancelButton: true,
                    preConfirm: () => {
                        return (document!.getElementById("time-input") as HTMLInputElement).value
                    }
                })
            })
            .then(result => {
                if (result.isDismissed) {
                    //dismissed dialog
                    return Promise.reject(new Error("closed"))
                }
                if (this.props.user.activeLocation === undefined || this.toDate === undefined) {
                    return Promise.reject(new Error("User has no activeLocation!"))
                }
                this.toDate = Utilities.parseTime(result.value!, this.toDate.toDate(), this.props.user.activeLocation.timezone)

                const now = moment()
                const startMoment = moment(this.fromDate)
                if (startMoment < now) {
                    this.fromDate = now
                }

                const endMoment = moment(this.toDate)
                const adjustedStartMoment = moment(this.fromDate)
                if (endMoment < adjustedStartMoment) {
                    return MySwal.fire({
                        title: "Invalid time range",
                        text: "End time cannot be earlier than start time. Please select a valid time range.",
                        icon: "warning",
                        confirmButtonText: "OK"
                    }).then(() => {
                        throw new Error("invalid-time-range")
                    })
                }

                MySwal.fire({
                    didOpen: () => {
                        Swal.showLoading()
                    }
                })
                return this.getAvailableSpotsForSelect()
            })
            .then(result => {
                if (!Object.entries(result).length) {
                    return Promise.reject(new Error("no-spots"))
                } else {
                    return MySwal.fire({
                        title: "Select parking spot",
                        input: "select",
                        inputOptions: result,
                        customClass: { input: "select_arrow y-style" },
                        confirmButtonText: "Next &rarr;",
                        showCancelButton: true
                    })
                }
            })
            .then(result => {
                if (!result.value) {
                    throw new Error("closed")
                }
                spotId = result.value
                const languageOptionsHtml = languages.map(lang => `<option value="${lang.id}">${lang.title}</option>`).join("")
                return MySwal.fire({
                    title: "Enter reservation details",
                    html:
                        '<input id="name" class="swal2-input y-style" placeholder="Visitor\'s name">' +
                        '<input id="surname" class="swal2-input y-style" placeholder="Visitor\'s surname *">' +
                        '<input id="license" class="swal2-input y-style" placeholder="License plate">' +
                        '<input id="visitor-company" class="swal2-input y-style" placeholder="Company">' +
                        '<input id="host" class="swal2-input y-style" placeholder="Visit for">' +
                        '<div class="swal2-checkbox mt-3">' +
                        '<input id="email-visitor" type="checkbox" class="swal2-checkbox">' +
                        '<label for="email-visitor">Send email to the visitor</label>' +
                        "</div>" +
                        '<div id="language-dropdown-container" style="display:none;">' +
                        '<input type="email" id="email" class="swal2-input y-style" placeholder="Visitor\'s email *">' +
                        `<select id="language" class="swal2-select y-style select_arrow" style="margin-top:1.2rem;padding:0.65rem 0.9rem;width:57%;">
                                ${languageOptionsHtml}
                             </select>` +
                        '<h5 class="mt-4">Send a copy to:</h5>' +
                        '<div class="mb-4" id="confirmation-emails-container"></div>' +
                        '<span id="add-confirmation-email" style="cursor: pointer;">➕ Add email address</span>' +
                        "</div>",
                    preConfirm: () => {
                        const name = $("#name").val() as string | undefined
                        const surname = $("#surname").val() as string | undefined
                        const license = $("#license").val() as string | undefined
                        const visitorCompany = $("#visitor-company").val() as string | undefined
                        const host = $("#host").val() as string | undefined
                        const sendEmail = $("#email-visitor").prop("checked")
                        const email = sendEmail ? ($("#email").val() as string | undefined) : undefined
                        const language = sendEmail ? ($("#language").val() as string | undefined) : undefined
                        const confirmationEmails = sendEmail
                            ? $(".confirmation-email")
                                  .map(function () {
                                      return $(this).val()
                                  })
                                  .get()
                                  .filter(email => email)
                            : []

                        if (!surname) {
                            Swal.showValidationMessage(`Please enter visitor's surname`)
                        }
                        if (sendEmail) {
                            if (!email) {
                                Swal.showValidationMessage(`Please enter visitor's email`)
                            } else if (!(document.getElementById("email") as HTMLInputElement).checkValidity()) {
                                Swal.showValidationMessage(`Please enter a valid email address`)
                            } else {
                                const invalidConfirmation = $(".confirmation-email")
                                    .toArray()
                                    .some(input => (input as HTMLInputElement).value && !(input as HTMLInputElement).checkValidity())
                                if (invalidConfirmation) {
                                    Swal.showValidationMessage(`Please enter valid confirmation email addresses`)
                                }
                            }
                        }
                        return { name, surname, email, license, visitorCompany, host, sendEmail, language, confirmationEmails }
                    },
                    didOpen: function () {
                        $("#email-visitor").on("change", function () {
                            if ($(this).is(":checked")) {
                                $("#language-dropdown-container").slideDown()
                            } else {
                                $("#language-dropdown-container").slideUp()
                            }
                        })
                        $("#add-confirmation-email").on("click", function () {
                            $("#confirmation-emails-container").append(
                                '<input type="email" class="swal2-input y-style confirmation-email" placeholder="Confirmation email">'
                            )
                        })
                    },
                    confirmButtonText: "Submit",
                    showCancelButton: true
                })
            })
            .then(result => {
                if (!result?.value) {
                    throw new Error("closed")
                }
                if (this.props.user.activeLocation === undefined || this.toDate === undefined) {
                    return Promise.reject(new Error("User has no activeLocation!"))
                }

                const userId = this.props.firebase.generateUserId()
                const user = User.create(
                    userId,
                    result.value?.name ?? "unknown",
                    result.value?.surname ?? "unknown",
                    result.value?.email ?? "unknown"
                )
                user.company = "visitor-" + this.props.user.company
                user.visitorCompany = result.value?.visitorCompany
                user.host = result.value?.host
                user.locale = result.value?.language

                const car = new Car({
                    id: this.props.firebase.generateCarId(userId),
                    plateNum: result.value?.license ?? "unknown",
                    engineType: EngineType.Gasoline,
                    deleted: false
                })

                const spot = this.state.parkingSpots!.find(spot => spot.id === spotId)!
                const reservationId = this.props.firebase.generateReservationId(this.props.user.activeLocation!.id)
                const reservation = new VisitorReservation({
                    id: reservationId,
                    userId: userId,
                    startTime: this.fromDate.toDate(),
                    endTime: this.toDate.toDate(),
                    confirmed: true,
                    canceled: false,
                    abused: false,
                    parkingSpot: spot.name ?? "unknown",
                    parkingSpotId: spotId,
                    priority: UserPriorityLevel.Visitors,
                    carId: car.id,
                    gateReservationsIds: [],
                    entryDates: [],
                    exitDates: [],
                    confirmationEmails: result.value?.confirmationEmails ?? [],
                    invitedBy: this.props.user.uid,
                    status: ReservationStatus.Confirmed
                })

                MySwal.fire({
                    didOpen: () => {
                        Swal.showLoading()
                    }
                })

                return Promise.all([
                    this.props.firebase.setUser(user),
                    this.props.firebase.createVisitorCar(car, userId),
                    this.props.firebase.setReservation(this.props.user.activeLocation, reservation, result.value?.sendEmail)
                ])
            })
            .then(_ => {
                this.fetchReservations()
            })
            .then(_ => {
                this.updateData()
                return MySwal.fire({
                    title: "Done",
                    text: "Visitor booking saved",
                    confirmButtonText: "OK",
                    icon: "success",
                    timer: 1500,
                    timerProgressBar: true
                })
            })
            .catch(error => {
                if (error.message === "closed") {
                    return
                } else if (error.message === "no-spots") {
                    return MySwal.fire({
                        title: "No spots",
                        text: "There are no available parking places for the selected time period.",
                        icon: "info"
                    })
                } else if (error.message === "invalid-time-range") {
                    return
                }
                MySwal.fire({
                    title: "Error",
                    text: "There was an error while adding booking. Please try again later." + error,
                    icon: "error"
                })
                console.warn("Error adding booking: ", error)
            })
    }

    handleDeleteBookingCLick = (reservation: Reservation) => {
        MySwal.fire({
            title: "Are you sure?",
            text: "Do you want to cancel reservation for " + reservation.user?.name + " " + reservation.user?.surname + "?",
            showCancelButton: true,
            cancelButtonText: "Go back",
            confirmButtonText: "Confirm",
            showLoaderOnConfirm: true,
            preConfirm: () => {
                if (!this.props.user.activeLocation) {
                    return Promise.reject(new Error("User has no activeLocation"))
                }
                return this.props.firebase
                    .cancelOrEndReservationAPI(this.props.user.activeLocation.id, reservation.id!)
                    .then(() => this.fetchReservations())
                    .catch(error => {
                        MySwal.fire({
                            title: "Error",
                            text: "There was an error while canceling the reservation. Please try again later.",
                            icon: "error"
                        })
                        console.warn("Error canceling reservation: ", error)
                    })
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed) {
                MySwal.fire({
                    title: "Successfully canceled the reservation",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 1500,
                    timerProgressBar: true
                })
            }
        })
    }

    render() {
        if (this.state.allowedLocations === undefined || this.state.visitorsReservations === undefined || this.state.parkingSpots === undefined) {
            return <div className="loader"></div>
        }

        if (this.state.allowedLocations.length === 0) {
            return (
                <div>
                    <br />
                    <h3>You are not authorized to view this page</h3>
                </div>
            )
        }

        const reservationsTable = (
            <div className="table-responsive">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Reservation start</th>
                            <th>Reservation end</th>
                            <th>Parking spot</th>
                            <th>Name</th>
                            <th>Surname</th>
                            <th>Email</th>
                            <th>Company</th>
                            <th>Visit for</th>
                            <th>License plate</th>
                            <th>Invited by</th>
                            <th>Remove</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.visitorsReservations.map((reservation, index) => {
                            const invitedByUser = this.state.invitedByUsers?.find(u => u.uid === reservation.invitedBy)
                            const car = reservation.user?.cars?.find(car => car.id === reservation.carId)

                            return (
                                <tr key={reservation.id}>
                                    <td>
                                        <span className="light-text">{index + 1}</span>
                                    </td>
                                    <td>
                                        {moment.tz(reservation.startTime, this.timezone).format("MMM D")}
                                        <br />
                                        <small className="text-muted">{moment.tz(reservation.startTime, this.timezone).format("H:mm")}</small>
                                    </td>
                                    <td>
                                        {moment.tz(reservation.endTime, this.timezone).format("MMM D")}
                                        <br />
                                        <small className="text-muted">{moment.tz(reservation.endTime, this.timezone).format("H:mm")}</small>
                                    </td>
                                    <td>{reservation.parkingSpot}</td>
                                    <td>{reservation.user?.name}</td>
                                    <td>{reservation.user?.surname}</td>
                                    <td>{reservation.user?.email}</td>
                                    <td>{reservation.user?.visitorCompany}</td>
                                    <td>{reservation.user?.host}</td>
                                    <td>{car?.plateNum}</td>
                                    <td>{invitedByUser ? `${invitedByUser.name} ${invitedByUser.surname}` : "-"}</td>
                                    <td>
                                        <Button color="error" size="small" onClick={() => this.handleDeleteBookingCLick(reservation)}>
                                            <DeleteForever />
                                        </Button>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )

        return (
            <div>
                {this.state.allowedLocations.length > 1 ? (
                    <div>
                        <span className="my-2 mr-2">Location:</span>
                        <select
                            className="mb-3 select_arrow y-style"
                            name="location"
                            id="location"
                            value={this.props.user.activeLocationId}
                            onChange={this.onSelectChange}
                        >
                            {this.state.allowedLocations
                                ?.sort((a, b) => a.title.localeCompare(b.title))
                                .map(location => {
                                    return (
                                        <option key={location.id} value={location.id}>
                                            {location.title}
                                        </option>
                                    )
                                })}
                        </select>
                    </div>
                ) : (
                    <div></div>
                )}
                <div>
                    <button onClick={this.handleAddBookingClick} className="my-2 btn btn-primary">
                        <FontAwesomeIcon icon={faPlus} /> Add booking
                    </button>
                </div>
                {reservationsTable}
                <Link
                    to={{
                        pathname: ROUTES.VISITORS_BOOKING_PREVIEW,
                        search: createSearchParams(
                            this.props.user.activeLocation !== undefined ? { location: this.props.user.activeLocation!.id } : {}
                        ).toString()
                    }}
                >
                    Preview
                </Link>
            </div>
        )
    }
}

const VisitorsBooking = (props: any) => (
    <div className="parkfair-container">
        <h3>Visitors booking</h3>
        <hr className="d-none d-md-block" />
        <UserStateCheck>
            {userInfo => (
                <Fade>
                    <VisitorsBookingBase user={userInfo.user} {...props} />
                </Fade>
            )}
        </UserStateCheck>
    </div>
)

export default injectIntl(withFirebase(VisitorsBooking))

function intersection(a: ParkingSpot[], b: ParkingSpot[]) {
    return a.filter(spotA => b.find(spotB => spotA.id === spotB.id) !== undefined)
}

interface IStaticDatePickerProps {
    date: moment.Moment
    onChange: (date: string | moment.Moment | null) => void
    timezone?: string
    locale?: string
    theme?: Theme
}

export const StaticDatePicker: FC<IStaticDatePickerProps> = ({ date, onChange, timezone, locale, theme }): ReactElement => {
    const onChangeSetValue = <T,>(newValue: moment.Moment | null, _: T) => {
        if (newValue === null) {
            return
        }
        setValue(newValue)
    }

    const [value, setValue] = useState(date)
    useEffect(() => {
        onChange(value)
    }, [value, onChange])

    return (
        <>
            <WrapIfDefined value={theme} wrapper={(theme, children) => <ThemeProvider theme={theme}>{children}</ThemeProvider>}>
                <WrapIfDefined
                    value={locale}
                    wrapper={(locale, children) => (
                        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                            {children}
                        </LocalizationProvider>
                    )}
                >
                    <DateField value={value} onChange={onChangeSetValue} timezone={timezone} required inputProps={{ className: "calendar" }} />
                    <DateCalendar value={value} onChange={onChangeSetValue} timezone={timezone} views={["day"]} />
                </WrapIfDefined>
            </WrapIfDefined>
        </>
    )
}

interface IWrapIfDefinedProps<T> {
    value: T | undefined
    wrapper: (value: T, children: ReactNode) => ReactNode
    children: ReactNode
}
const WrapIfDefined = <T,>({ value, wrapper, children }: IWrapIfDefinedProps<T>): ReactNode =>
    value !== undefined ? wrapper(value, children) : children
